import { Injectable } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { Observable, BehaviorSubject } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

export interface SocketEvent {
  type: string;
  data: any;
}

@Injectable({
  providedIn: 'root'
})
export class SocketService {
  private socket: Socket | null = null;
  private connectedSubject = new BehaviorSubject<boolean>(false);
  public connected$ = this.connectedSubject.asObservable();

  constructor(private authService: AuthService) {
    this.authService.isAuthenticated$.subscribe(isAuth => {
      if (isAuth) {
        this.connect();
      } else {
        this.disconnect();
      }
    });
  }

  private connect(): void {
    if (this.socket) {
      return;
    }

    const user = this.authService.getCurrentUser();
    if (!user) {
      return;
    }

    this.socket = io(environment.socketUrl, {
      auth: {
        token: this.authService.getToken()
      }
    });

    this.socket.on('connect', () => {
      console.log('Socket connected');
      this.connectedSubject.next(true);
      
      // Join user room
      if (user) {
        this.socket?.emit('join-room', user.id);
      }
    });

    this.socket.on('disconnect', () => {
      console.log('Socket disconnected');
      this.connectedSubject.next(false);
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      this.connectedSubject.next(false);
    });
  }

  private disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connectedSubject.next(false);
    }
  }

  // Listen for specific events
  on(eventName: string): Observable<any> {
    return new Observable(observer => {
      if (!this.socket) {
        observer.error('Socket not connected');
        return;
      }

      this.socket.on(eventName, (data: any) => {
        observer.next(data);
      });

      return () => {
        if (this.socket) {
          this.socket.off(eventName);
        }
      };
    });
  }

  // Emit events
  emit(eventName: string, data?: any): void {
    if (this.socket) {
      this.socket.emit(eventName, data);
    }
  }

  // Specific event listeners for the application
  onNewOrder(): Observable<any> {
    return this.on('new_order');
  }

  onFileUploaded(): Observable<any> {
    return this.on('file_uploaded');
  }

  onOrderStatusChanged(): Observable<any> {
    return this.on('order_status_changed');
  }

  onOrderReviewed(): Observable<any> {
    return this.on('order_reviewed');
  }

  onOrderCancelled(): Observable<any> {
    return this.on('order_cancelled');
  }

  isConnected(): boolean {
    return this.connectedSubject.value;
  }
}